import React from "react";
import { Box, Typography } from "@mui/material";
import Button from "@components/Button/Button";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import TotalQuantityRemained, { RemainedWarning } from "./TotalQuantityRemained";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.add`;

/**
 * DBCO-0029 - the read-only totals block of the Operations info: Total quantity of operations (with an
 * Edit button where the page allows editing) and Total quantity remained, both as the server returned
 * them, then the direction warning. The operation page shows it without `onEdit`.
 */
const OperationTotals: React.FC<{
  totalQuantity: number | null;
  remained: number | null;
  /** False for Operations: the wireframe's Operations info shows the total only. */
  showRemained?: boolean;
  /** True inside the Operations info card, where the card row carries the label. */
  inCard?: boolean;
  onEdit?: () => void;
}> = ({ totalQuantity, remained, showRemained = true, inCard = false, onEdit }) => {
  const { t } = useTranslation();
  const value = totalQuantity === null ? "-" : String(totalQuantity);
  const editButton = onEdit && (
    <Button
      variant="outlined"
      size="small"
      testId="drybulk-cargo-operation-total-edit"
      onClick={onEdit}
      label={<span className="normal-case font-medium">{t(`${L}.edit`)}</span>}
    />
  );
  if (inCard) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }} data-testid="operation-totals">
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography variant="body2" fontWeight={500} data-testid="operation-total-quantity">
            {value}
          </Typography>
          {editButton}
          {showRemained && <TotalQuantityRemained value={remained} />}
        </Box>
        {showRemained && <RemainedWarning value={remained} />}
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid="operation-totals">
      <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {t(`${L}.total-quantity-of-operations`)}
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} data-testid="operation-total-quantity">
              {value}
            </Typography>
          </Box>
          {editButton}
        </Box>
        {showRemained && <TotalQuantityRemained value={remained} />}
      </Box>
      {showRemained && <RemainedWarning value={remained} />}
    </Box>
  );
};

export default OperationTotals;
